import { Fragment, useState } from "react";
import {
  IonGrid,
  IonRow,
  IonCol,
  IonAvatar,
  IonButton,
  IonIcon,
  IonItem,
} from "@ionic/react";
import { Popover } from "@mui/material";
import { useIcons } from "./useIcons";
import { close, refresh } from "ionicons/icons";
import { Spinner } from "pymble-ui/components";

interface IconItem {
  img?: string;
  [key: string]: unknown;
}

export const PopoverIconList: React.FC<{
  onHide: () => void;
  icons: IconItem[];
  onSelect: (icon: IconItem) => void;
  onRefresh?: () => void;
  loading?: boolean;
}> = ({ onHide, icons, onSelect, onRefresh, loading }) => {
  return (
    <Fragment>
      <IonGrid className="icon-popover-grid">
        <IonRow>
          {icons &&
            icons.map((icon, i: number) => {
              return (
                <IonCol key={i} onClick={() => onSelect(icon)}>
                  <IonAvatar>
                    <img src={icon.img} alt="icon" />
                  </IonAvatar>
                </IonCol>
              );
            })}
        </IonRow>
        <IonRow>
          <IonCol>
            <IonItem lines="none">
              {loading && <Spinner />}
              <IonButton
                onClick={onRefresh}
                className="ion-float-right ion-text-capitalize"
                fill="outline"
                slot="end"
              >
                Refresh
                <IonIcon icon={refresh} slot="start" />
              </IonButton>
              <IonButton
                onClick={onHide}
                className="ion-float-right ion-text-capitalize"
                slot="end"
                fill="outline"
              >
                Close
                <IonIcon icon={close} slot="start" />
              </IonButton>
            </IonItem>
          </IonCol>
        </IonRow>
      </IonGrid>
    </Fragment>
  );
};

/**
 * Hook to select group types
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook is co-located with its popover component
export function useSelectIconPopover() {
  const { icons, iconsLoaded, refreshIcons } = useIcons();

  const [selectedIcon, setSelectedIcon] = useState<IconItem>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const dismissIcons = () => {
    setAnchorEl(null);
  };

  const handleSelectIcon = (icon: IconItem) => {
    setSelectedIcon(icon);
    dismissIcons();
  };

  const presentIcons = (opts: { event: React.MouseEvent | Event }) => {
    const target = opts.event?.target || (opts.event as React.MouseEvent)?.currentTarget || null;
    setAnchorEl(target as HTMLElement | null);
  };

  const IconsPopover = (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={dismissIcons}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{ zIndex: 30000 }}
    >
      <PopoverIconList
        onHide={dismissIcons}
        icons={icons as IconItem[]}
        onSelect={handleSelectIcon}
        onRefresh={refreshIcons}
        loading={!iconsLoaded}
      />
    </Popover>
  );

  return {
    presentIcons,
    dismissIcons,
    handleSelectIcon,
    selectedIcon,
    PopoverIconList,
    iconsLoaded,
    IconsPopover,
  };
}
